import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useState, useEffect } from "react";
import Checkbox from "../util/Checkbox";
import CreateGroceryList from "./CreateGroceryList";
import useLoginCheck from "../util/useLoginCheck";
import GroceryItem from "../recipes/GroceryItem";
import Spinner from "../util/Spinner";
import { fetchRecipes } from "../slice/recipeSlice";
import { fetchMeasurements } from "../slice/measurementSlice";
import { fetchIngredients } from "../slice/ingredientsSlice";
import AutocompleteButton from "../util/AutocompleteButton";

const GroceryList = () => {
  const dispatch = useDispatch();

  const { recipes, isLoading, error } = useSelector((state) => {
    return state?.recipes;
  });
  const { measurements, isLoadingMeasurements } = useSelector((state) => {
    return state?.measurements;
  });
  const { ingredients, isLoadingIngredients } = useSelector((state) => {
    return state?.ingredients;
  });
  const [recipeSids, setRecipeSids] = useState([]);
  const [searchParams] = useSearchParams();
  const [isCreateGroceryListOpen, setIsCreateGroceryListOpen] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedGroceryItem, setSelectedGroceryItem] = useState("");

  const [groceryItems, setGroceryItems] = useState([]);

  const location = useLocation();
  const navigate = useNavigate();

  useLoginCheck();

  useEffect(() => {
    console.log("fetching measurements and ingredients");
    dispatch(fetchMeasurements());
    dispatch(fetchIngredients());
  }, [dispatch]);

  useEffect(() => {
    const refreshParam = searchParams.get("refresh") === "true";
    console.log("refreshParam is ", refreshParam);
    if (refreshParam) {
      console.log("refreshing recipes...");
      dispatch(fetchRecipes());
      navigate(window.location.pathname, { replace: true }); // clear query string
    } else {
      console.log("fetching recipes...");
      dispatch(fetchRecipes());
    }
  }, [dispatch, location.search]);

  const enableCreateList = () => {
    return !isDisabled || groceryItems.length > 0;
  };

  const handleChange = (sid) => {
    if (!recipeSids.includes(sid)) {
      console.log("adding recipeSid to grocery list ", sid);
      setRecipeSids((prev) => [...prev, sid]);
      setIsDisabled(false);
    } else {
      console.log("removing recipeSid from grocery list ", sid);
      setRecipeSids((prev) => prev.filter((recipeSid) => recipeSid !== sid));
    }
  };

  const toggleCreateGroceryList = () => {
    setIsCreateGroceryListOpen(!isCreateGroceryListOpen);
  };

  const handleSearchTextChange = (text) => {
    console.log("Search text: ", text);
    setSearchText(text);
  };

  const recipeNameMatchesSearch = (recipe) => {
    return searchText
      ? recipe?.name?.toLowerCase().includes(searchText.toLowerCase())
      : true;
  };

  const renderCheckbox = (recipe) => {
    return (
      <li key={recipe.sid}>
        <Checkbox
          key={recipe?.sid}
          id={recipe?.sid}
          checked={recipeSids.includes(recipe?.sid)}
          onCheck={handleChange}
          label={recipe?.name}
        ></Checkbox>
      </li>
    );
  };

  useEffect(() => {
    console.log("Updated recipeSids: ", recipeSids);
    if (recipeSids.length === 0) {
      console.log("No recipe sids selected, disabling button");
      setIsDisabled(true);
    }
  }, [recipeSids]); // runs whenever recipeSids changes

  const removeGroceryItem = (item) => {
    console.log("removing item from grocery list: ", item);
    setGroceryItems((prev) =>
      prev.filter((i) => i?.ingredient !== item?.ingredient)
    );
  };

  const updateMeasurement = (groceryItem, measurement) => {
    console.log(
      "CreateGroceryList updating " +
        groceryItem?.ingredient +
        " measurement to " +
        measurement
    );
    setGroceryItems((prevItems) =>
      prevItems.map((item) =>
        groceryItem?.ingredient === item?.ingredient
          ? {
              ...item,
              measurement,
            }
          : item
      )
    );
  };

  const updateQuantity = (groceryItem, quantity) => {
    console.log(
      "CreateGroceryList updating " +
        groceryItem?.ingredient +
        " quantity to " +
        quantity
    );
    setGroceryItems((prevItems) =>
      prevItems.map((item) =>
        groceryItem?.ingredient === item?.ingredient
          ? {
              ...item,
              quantity,
            }
          : item
      )
    );
  };

  const addGroceryItem = () => {
    const hasItem = groceryItems.some(
      (item) => item.ingredient === selectedGroceryItem
    );
    if (!hasItem) {
      console.log("Adding grocery item to list ", selectedGroceryItem);
      setGroceryItems((prevList) => [
        { ingredient: selectedGroceryItem, quantity: 1 },
        ...prevList,
      ]);
    } else {
      console.log(
        "Not adding grocery item " +
          selectedGroceryItem +
          " to list--it's already there!"
      );
    }
    setSelectedGroceryItem("");
  };

  return (
    <div className="max-w-2xl mx-auto p-4 md:p-6">
      {isLoading && isLoadingMeasurements && isLoadingIngredients ? (
        <Spinner />
      ) : (
        <>
          {isCreateGroceryListOpen ? (
            <CreateGroceryList
              closePopup={toggleCreateGroceryList}
              recipeSids={recipeSids}
              requestedGroceryItems={groceryItems}
              measurements={measurements}
              ingredients={ingredients}
            />
          ) : (
            <div className="card p-4 md:p-6 w-full">
              <h2 className="text-lg font-semibold text-stone-800 mb-4">
                Create grocery list
              </h2>
              <div className="text-sm font-medium text-stone-500 mb-2">
                Add items
              </div>
              <div>
                <div className="mb-3">
                  {ingredients && (
                    <AutocompleteButton
                      items={ingredients}
                      updateItems={(i) => setSelectedGroceryItem(i?.name)}
                      buttonHandler={addGroceryItem}
                    />
                  )}
                </div>
                <ul className="space-y-2">
                  {groceryItems.map((groceryItem, index) => {
                    return (
                      <GroceryItem
                        key={index}
                        groceryItem={groceryItem}
                        removeGroceryItem={removeGroceryItem}
                        measurements={measurements}
                        updateMeasurement={updateMeasurement}
                        updateQuantity={updateQuantity}
                      />
                    );
                  })}
                </ul>
              </div>
              <hr className="border-stone-200 my-4" />
              <div className="text-sm font-medium text-stone-500 mb-2">
                Add recipes
              </div>
              <input
                type="text"
                placeholder="Search..."
                className="w-full border border-stone-300 rounded-md px-3 py-2 mb-3 focus:outline-none focus:ring-2 focus:ring-teal-600"
                value={searchText}
                onChange={(e) => handleSearchTextChange(e.target.value)}
              ></input>
              <ul className="space-y-1">
                {recipes?.filter(recipeNameMatchesSearch).map(renderCheckbox)}
              </ul>
              <div className="flex justify-end mt-4">
                <button
                  className="btn-primary"
                  onClick={toggleCreateGroceryList}
                  disabled={!enableCreateList()}
                >
                  Create
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GroceryList;
